import { DurationInputArg1, DurationInputArg2 } from 'moment';

import { getAppConfig } from '../apiClient';
import { getAppConfig as getAppVendorConfig } from '../vendorPanel/apiClient';
import { getAppConfig as getAppPartnerConfig } from '../partnerPanel/apiClient';
import Action from '../action';
import { countries } from '../utils/countries';
import { windowStorage } from '../windowStorage';
import { ProductTabs } from '../products/helpers';
import { UserStatuses } from '../utils/users';

export interface Permission {
    permissionName: string;
    metadata: {
        displayText: string;
        explanation: string;
        requiredPermissions: string[];
        section: string;
    };
}
export class AppConfigState {
    entitlementsConfig: EntitlementCategoriesConfig;
    sapConfig: SapConfig;
    entitlementCurrencyConfig: EntitlementCurrencyConfig;
    paymentConfig: PaymentConfig;
    country: countries;
    location: { isActive: boolean };
    timeZone: string;
    permissions: {
        [key: string]: Permission[];
    };
    initialTransactionDate: string;
    project: ProjectType;
    productManagementConfig: { maxPriceValue: number; productsActiveTab: ProductTabs; priceSchedulingEnabled: boolean };
    userStatuses: {
        allStatus: UserStatuses[];
        managerStatus: UserStatuses[];
        vendorStatus: UserStatuses[];
        vendorUserStatus: UserStatuses[];
        partnerStatus: UserStatuses[];
        partnerUserStatus: UserStatuses[];
    };
    allowAddingBranches: boolean;
    awsConfig?: { publicAssetsCDN: string };

    constructor(appConfig: AppConfigState) {
        this.entitlementsConfig = appConfig.entitlementsConfig;
        this.sapConfig = appConfig.sapConfig;
        this.entitlementCurrencyConfig = appConfig.entitlementCurrencyConfig;
        this.paymentConfig = appConfig.paymentConfig;
        this.country = appConfig.country;
        this.location = appConfig.location;
        this.timeZone = appConfig.timeZone;
        this.permissions = appConfig.permissions;
        this.initialTransactionDate = appConfig.initialTransactionDate;
        this.project = appConfig.project;
        this.userStatuses = appConfig.userStatuses;
        this.productManagementConfig = appConfig.productManagementConfig;
        this.allowAddingBranches = appConfig.allowAddingBranches;
        this.awsConfig = (appConfig as AppConfigState).awsConfig;
    }
}

export class SapConfig {
    isSapActive: boolean;
    sapDocumentChainMinimumAmount: string;
    maxPOnumber: number;

    constructor(isSapActive = false, sapDocumentChainMinimumAmount = '100', maxPOnumber = 20) {
        this.isSapActive = isSapActive;
        this.sapDocumentChainMinimumAmount = sapDocumentChainMinimumAmount;
        this.maxPOnumber = maxPOnumber;
    }
}

export class EntitlementCategoriesConfig {
    categories: string[];
    abbreviations: string[];
    abbreviationsToCategories: Record<string, string>;

    constructor(
        categories: string[] = [],
        abbreviations: string[] = [],
        abbreviationsToCategories: Record<string, string> = {}
    ) {
        this.categories = categories;
        this.abbreviations = abbreviations;
        this.abbreviationsToCategories = abbreviationsToCategories;
    }
}

export interface PredefinedCurrency {
    name: string;
    fractionDigits: number;
}

export class EntitlementCurrencyConfig implements PredefinedCurrency {
    name: string;
    fractionDigits: number;

    constructor(name = '', fractionDigits = 3) {
        this.name = name;
        this.fractionDigits = fractionDigits;
    }
}

export class PaymentConfig {
    initialPeriodEnd: string;
    duration: PaymentDuration;
    mergeArraysSize: string;
}

export interface PaymentDuration {
    amount: DurationInputArg1;
    unit: DurationInputArg2;
}

export enum ProjectType {
    default = '/',
    dhaka = '/dhaka/',
}

export const projectTypeNaming = {
    [ProjectType.dhaka]: 'DHAKA',
};

export const AppConfigActionTypes = {
    loadAppConfig: 'AppConfig.loadAppConfig',
    loadProjectType: 'AppConfig.loadProjectType',
    changeProductTab: 'AppConfig.changeProductTab',
};

export const AppConfigActionCreators = {
    loadApplicationConfig(isVendor?: boolean, isPartner?: boolean) {
        let getConfig;
        if (isVendor) {
            getConfig = getAppVendorConfig;
        } else if (isPartner) {
            getConfig = getAppPartnerConfig;
        } else {
            getConfig = getAppConfig;
        }
        return (dispatch) => {
            return getConfig().then((res) => {
                dispatch({
                    type: AppConfigActionTypes.loadAppConfig,
                    payload: res,
                });
            });
        };
    },
    changeProjectType() {
        // dhaka turn off
        return () => {};

        // return (dispatch) => {
        //     dispatch({
        //         type: AppConfigActionTypes.loadProjectType,
        //         payload: projectType,
        //     });
        //     windowStorage.setItem('projectType', projectType);
        // };
    },

    changeProjectTypeBasedOnUrl() {
        // dhaka turn off
        return () => {};

        // return (dispatch) => {
        //     const search = new URLSearchParams(window.location.search);
        //     const projectType = atob(search.get('p'));
        //     dispatch({
        //         type: AppConfigActionTypes.loadProjectType,
        //         payload: projectType,
        //     });
        //     windowStorage.setItem('projectType', projectType);
        // };
    },
};

export function appConfigReducer(
    state: AppConfigState = new AppConfigState({
        entitlementsConfig: new EntitlementCategoriesConfig(),
        sapConfig: new SapConfig(),
        entitlementCurrencyConfig: new EntitlementCurrencyConfig(),
        paymentConfig: new PaymentConfig(),
        country: window.location.href.includes('bgd') ? countries.BANGLADESH : countries.JORDAN,
        location: { isActive: false },
        timeZone: 'utc',
        permissions: {},
        initialTransactionDate: '',
        project: getProjectUrl(),
        userStatuses: {
            allStatus: [],
            managerStatus: [],
            vendorStatus: [],
            vendorUserStatus: [],
            partnerStatus: [],
            partnerUserStatus: [],
        },
        productManagementConfig: {
            maxPriceValue: Number.MAX_SAFE_INTEGER,
            productsActiveTab: ProductTabs.Prices,
            priceSchedulingEnabled: false,
        },
        allowAddingBranches: false,
    }),
    action: Action
) {
    switch (action.type) {
        case AppConfigActionTypes.loadAppConfig:
            return new AppConfigState({ ...(action.payload as AppConfigState), project: state.project });
        case AppConfigActionTypes.loadProjectType:
            return new AppConfigState({ ...state, project: action.payload as AppConfigState['project'] });
        case AppConfigActionTypes.changeProductTab:
            return new AppConfigState({
                ...state,
                productManagementConfig: {
                    ...state.productManagementConfig,
                    productsActiveTab: action.payload as ProductTabs,
                },
            });
    }
    return state;
}

export function getProjectUrl(): ProjectType {
    return (windowStorage.getItem('projectType') as ProjectType) || ProjectType.default;
}
